// Đại vận dataset v2.1 — expanded relations + hidden stems + ten gods (deterministic)

export const STEMS = ['Giáp','Ất','Bính','Đinh','Mậu','Kỷ','Canh','Tân','Nhâm','Quý'];
export const BRANCHES = ['Tý','Sửu','Dần','Mão','Thìn','Tỵ','Ngọ','Mùi','Thân','Dậu','Tuất','Hợi'];

const mod = (n, m) => ((n % m) + m) % m;
const MS_PER_DAY = 86400000;
const DAYS_PER_TROPICAL_YEAR = 365.2425;

// ===== Yin/Yang =====
const YANG_STEM_INDEX = new Set([0, 2, 4, 6, 8]);
const YANG_BRANCH_INDEX = new Set([0, 2, 4, 6, 8, 10]);

export const stemYinYang = (canIndex) => (YANG_STEM_INDEX.has(canIndex) ? 'yang' : 'yin');
export const branchYinYang = (chiIndex) => (YANG_BRANCH_INDEX.has(chiIndex) ? 'yang' : 'yin');

// ===== Five Elements =====
const STEM_ELEMENT = {
  Giáp: 'Mộc', Ất: 'Mộc',
  Bính: 'Hỏa', Đinh: 'Hỏa',
  Mậu: 'Thổ', Kỷ: 'Thổ',
  Canh: 'Kim', Tân: 'Kim',
  Nhâm: 'Thủy', Quý: 'Thủy'
};

const BRANCH_ELEMENT = {
  Tý: 'Thủy', Sửu: 'Thổ', Dần: 'Mộc', Mão: 'Mộc',
  Thìn: 'Thổ', Tỵ: 'Hỏa', Ngọ: 'Hỏa', Mùi: 'Thổ',
  Thân: 'Kim', Dậu: 'Kim', Tuất: 'Thổ', Hợi: 'Thủy'
};

export const stemElement = (canIndex) => STEM_ELEMENT[STEMS[canIndex]];
export const branchElement = (chiIndex) => BRANCH_ELEMENT[BRANCHES[chiIndex]];

const normalizeBinaryGender = (gender) => {
  const g = (gender || '').toString().trim().toLowerCase();
  if (['nam', 'male', 'm'].includes(g)) return 'male';
  if (['nu', 'nữ', 'female', 'f'].includes(g)) return 'female';
  return null;
};

function parseDateSafe(x) {
  if (!x) return null;
  const d = x instanceof Date ? x : new Date(x);
  return Number.isFinite(d.getTime()) ? d : null;
}

function shiftUTCYear(date, deltaYears) {
  const shifted = new Date(date.getTime());
  shifted.setUTCFullYear(shifted.getUTCFullYear() + deltaYears);
  return shifted;
}

function formatDateWithOffset(date, tzOffsetMinutes = 0) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return null;
  const offsetMs = tzOffsetMinutes * 60000;
  const localMs = date.getTime() + offsetMs;
  const local = new Date(localMs);
  const iso = local.toISOString().slice(0, 19);
  const sign = tzOffsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(tzOffsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${iso}${sign}${hh}:${mm}`;
}

function normalizeSolarTermsAroundBirth({ birthAtLocal, prevTerm, nextTerm, tzOffsetMinutes }) {
  const warnings = [];
  const birth = parseDateSafe(birthAtLocal);
  const prev = parseDateSafe(prevTerm?.at_local);
  const next = parseDateSafe(nextTerm?.at_local);

  if (!birth || !prev || !next) {
    return { prevTerm, nextTerm, warnings: ['missing_prev_next_solar_terms'] };
  }

  let prevD = prev;
  let nextD = next;

  let wrappedNext = 0;
  while (nextD.getTime() <= birth.getTime() && wrappedNext < 3) {
    nextD = shiftUTCYear(nextD, 1);
    wrappedNext += 1;
  }
  const nextCrossedCalendarYear = nextD.getUTCFullYear() > birth.getUTCFullYear();
  if (wrappedNext > 0 || nextCrossedCalendarYear) warnings.push('solar_term_next_wrapped_year');

  let wrappedPrev = 0;
  while (prevD.getTime() >= birth.getTime() && wrappedPrev < 3) {
    prevD = shiftUTCYear(prevD, -1);
    wrappedPrev += 1;
  }
  if (wrappedPrev > 0) warnings.push('solar_term_prev_wrapped_year');

  if (!(prevD.getTime() < birth.getTime())) warnings.push('prev_term_not_before_birth');
  if (!(birth.getTime() < nextD.getTime())) warnings.push('next_term_not_after_birth');

  const normalizedPrev = {
    ...(prevTerm || {}),
    at_local: formatDateWithOffset(prevD, tzOffsetMinutes) || prevTerm?.at_local || null
  };

  const normalizedNext = {
    ...(nextTerm || {}),
    at_local: formatDateWithOffset(nextD, tzOffsetMinutes) || nextTerm?.at_local || null
  };

  return { prevTerm: normalizedPrev, nextTerm: normalizedNext, warnings };
}

function mergeBranchRelations(relations) {
  const merged = {};
  (relations || []).forEach((r) => {
    if (!r?.type || !r?.pair) return;
    const elementKey = r.element ? `|${r.element}` : '';
    const key = `${r.type}:${r.pair[0]}-${r.pair[1]}${elementKey}`;
    if (!merged[key]) {
      merged[key] = { ...r, hits: [] };
    }
    if (Array.isArray(r.hits)) {
      merged[key].hits.push(...r.hits);
    }
  });

  return Object.values(merged).map((item) => ({
    ...item,
    hits: Array.from(new Set(item.hits))
  }));
}

// ===== Chi relations (Xung/Hại/Lục hợp) =====
const CHI_XUNG = new Map([
  ['Tý','Ngọ'], ['Ngọ','Tý'],
  ['Sửu','Mùi'], ['Mùi','Sửu'],
  ['Dần','Thân'], ['Thân','Dần'],
  ['Mão','Dậu'], ['Dậu','Mão'],
  ['Thìn','Tuất'], ['Tuất','Thìn'],
  ['Tỵ','Hợi'], ['Hợi','Tỵ']
]);

const CHI_HAI = new Map([
  ['Tý','Mùi'], ['Mùi','Tý'],
  ['Sửu','Ngọ'], ['Ngọ','Sửu'],
  ['Dần','Tỵ'], ['Tỵ','Dần'],
  ['Mão','Thìn'], ['Thìn','Mão'],
  ['Thân','Hợi'], ['Hợi','Thân'],
  ['Dậu','Tuất'], ['Tuất','Dậu']
]);

const CHI_LUC_HOP = new Map([
  ['Tý','Sửu'], ['Sửu','Tý'],
  ['Dần','Hợi'], ['Hợi','Dần'],
  ['Mão','Tuất'], ['Tuất','Mão'],
  ['Thìn','Dậu'], ['Dậu','Thìn'],
  ['Tỵ','Thân'], ['Thân','Tỵ'],
  ['Ngọ','Mùi'], ['Mùi','Ngọ']
]);

const CHI_LUC_HOP_ELEMENT = new Map([
  ['Tý-Sửu', 'Thổ'],
  ['Dần-Hợi', 'Mộc'],
  ['Mão-Tuất', 'Hỏa'],
  ['Thìn-Dậu', 'Kim'],
  ['Tỵ-Thân', 'Thủy'],
  ['Ngọ-Mùi', 'Thổ']
]);

function relationChiBasic(a, b) {
  if (CHI_XUNG.get(a) === b) return 'xung';
  if (CHI_HAI.get(a) === b) return 'hai';
  if (CHI_LUC_HOP.get(a) === b) return 'hop';
  return null;
}

function getLucHopElement(a, b) {
  const key = `${a}-${b}`;
  const reversedKey = `${b}-${a}`;
  return CHI_LUC_HOP_ELEMENT.get(key) || CHI_LUC_HOP_ELEMENT.get(reversedKey) || null;
}

// ===== Tam hợp (3 chi / 4 cục) + Bán hợp =====
const TAM_HOP_GROUPS = [
  { element: 'Thủy', branches: ['Thân','Tý','Thìn'] },
  { element: 'Mộc',  branches: ['Hợi','Mão','Mùi'] },
  { element: 'Hỏa',  branches: ['Dần','Ngọ','Tuất'] },
  { element: 'Kim',  branches: ['Tỵ','Dậu','Sửu'] }
];

function findTamHopGroup(chiName) {
  return TAM_HOP_GROUPS.find((g) => g.branches.includes(chiName)) || null;
}

/**
 * Return:
 * - full: {type:'tam_hop', group:[...], element, hits:[pillars], missing:[] } when chi_van + natal cover all 3
 * - half: list of {type:'ban_hop', pair:[chi_van, natalChi], element, hits:[pillars]}
 */
function computeTamHopRelations(chiVan, natalBranches /* array {pillar, chi} */) {
  const group = findTamHopGroup(chiVan);
  if (!group) return { full: null, half: [] };

  const natalInGroup = natalBranches
    .filter((nb) => group.branches.includes(nb.chi))
    .map((nb) => ({ pillar: nb.pillar, chi: nb.chi }));

  const natalChiSet = new Set(natalInGroup.map((x) => x.chi));

  const present = new Set([chiVan, ...Array.from(natalChiSet)]);
  const missing = group.branches.filter((c) => !present.has(c));

  const full =
    missing.length === 0
      ? {
          type: 'tam_hop',
          group: group.branches.slice(),
          element: group.element,
          hits: natalInGroup
            .filter((x) => x.chi !== chiVan)
            .map((x) => x.pillar),
          missing: []
        }
      : null;

  const half = natalInGroup
    .filter((x) => x.chi !== chiVan)
    .map((x) => ({
      type: 'ban_hop',
      pair: [chiVan, x.chi],
      element: group.element,
      hits: [x.pillar]
    }));

  return { full, half };
}

// ===== Thiên Can Hợp (5 hợp) =====
// Giáp+Kỷ, Ất+Canh, Bính+Tân, Đinh+Nhâm, Mậu+Quý
const STEM_HOP_PAIRS = [
  ['Giáp','Kỷ'],
  ['Ất','Canh'],
  ['Bính','Tân'],
  ['Đinh','Nhâm'],
  ['Mậu','Quý']
];

function isStemHop(a, b) {
  return STEM_HOP_PAIRS.some(([x, y]) => (a === x && b === y) || (a === y && b === x));
}

function computeStemHopWithNatal(canVan, natalStems /* array {pillar, can} */) {
  const hits = [];
  for (const ns of natalStems) {
    if (isStemHop(canVan, ns.can)) {
      hits.push({
        type: 'hop',
        pair: [canVan, ns.can],
        hits: [ns.pillar]
      });
    }
  }
  return hits;
}

// ===== Tàng Can table (Hidden Stems) =====
const HIDDEN_STEMS_BY_BRANCH = {
  'Tý':  [{ stem: 'Quý', weight: 1.0 }],
  'Sửu': [{ stem: 'Kỷ', weight: 0.6 }, { stem: 'Quý', weight: 0.3 }, { stem: 'Tân', weight: 0.1 }],
  'Dần': [{ stem: 'Giáp', weight: 0.6 }, { stem: 'Bính', weight: 0.3 }, { stem: 'Mậu', weight: 0.1 }],
  'Mão': [{ stem: 'Ất', weight: 1.0 }],
  'Thìn': [{ stem: 'Mậu', weight: 0.6 }, { stem: 'Ất', weight: 0.3 }, { stem: 'Quý', weight: 0.1 }],
  'Tỵ':  [{ stem: 'Bính', weight: 0.6 }, { stem: 'Mậu', weight: 0.3 }, { stem: 'Canh', weight: 0.1 }],
  'Ngọ': [{ stem: 'Đinh', weight: 0.6 }, { stem: 'Kỷ', weight: 0.4 }],
  'Mùi': [{ stem: 'Kỷ', weight: 0.6 }, { stem: 'Đinh', weight: 0.3 }, { stem: 'Ất', weight: 0.1 }],
  'Thân':[{ stem: 'Canh', weight: 0.6 }, { stem: 'Nhâm', weight: 0.3 }, { stem: 'Mậu', weight: 0.1 }],
  'Dậu': [{ stem: 'Tân', weight: 1.0 }],
  'Tuất':[{ stem: 'Mậu', weight: 0.6 }, { stem: 'Tân', weight: 0.3 }, { stem: 'Đinh', weight: 0.1 }],
  'Hợi': [{ stem: 'Nhâm', weight: 0.6 }, { stem: 'Giáp', weight: 0.4 }]
};

function stemIndexByName(stemName) {
  const idx = STEMS.indexOf(stemName);
  return idx >= 0 ? idx : null;
}

function computeHiddenStemsForBranch(chiName, dayCanIndex) {
  const list = HIDDEN_STEMS_BY_BRANCH[chiName] || [];
  return list
    .map((item) => {
      const canIndex = stemIndexByName(item.stem);
      if (canIndex == null) return null;
      return {
        stem: item.stem,
        canIndex,
        weight: item.weight,
        element: STEM_ELEMENT[item.stem],
        yinyang: stemYinYang(canIndex),
        ten_god: tenGodStem(dayCanIndex, canIndex)
      };
    })
    .filter(Boolean);
}

// ===== Ten Gods for STEM vs DayMaster STEM =====
const E_ORDER = ['Mộc','Hỏa','Thổ','Kim','Thủy'];
const idxE = (e) => E_ORDER.indexOf(e);
const gen = (e) => E_ORDER[mod(idxE(e) + 1, 5)];
const ctrl = (e) => E_ORDER[mod(idxE(e) + 2, 5)];
const controlledBy = (e) => E_ORDER[mod(idxE(e) - 2, 5)];
const generatedBy = (e) => E_ORDER[mod(idxE(e) - 1, 5)];

function tenGodStem(dayCanIndex, targetCanIndex) {
  const dmStem = STEMS[dayCanIndex];
  const tgStem = STEMS[targetCanIndex];
  const dmE = STEM_ELEMENT[dmStem];
  const tgE = STEM_ELEMENT[tgStem];
  const dmYY = stemYinYang(dayCanIndex);
  const tgYY = stemYinYang(targetCanIndex);
  const samePolarity = dmYY === tgYY;

  if (dmE === tgE) {
    return {
      name: samePolarity ? 'Tỷ Kiên' : 'Kiếp Tài',
      category: 'Friend',
      reason: `cùng hành ${dmE}, ${samePolarity ? 'cùng' : 'khác'} âm dương`
    };
  }
  if (gen(dmE) === tgE) {
    return {
      name: samePolarity ? 'Thực Thần' : 'Thương Quan',
      category: 'Output',
      reason: `${dmE} sinh ${tgE}, ${samePolarity ? 'cùng' : 'khác'} âm dương`
    };
  }
  if (ctrl(dmE) === tgE) {
    return {
      name: samePolarity ? 'Thiên Tài' : 'Chính Tài',
      category: 'Wealth',
      reason: `${dmE} khắc ${tgE}, ${samePolarity ? 'cùng' : 'khác'} âm dương`
    };
  }
  if (controlledBy(dmE) === tgE) {
    return {
      name: samePolarity ? 'Thiên Quan' : 'Chính Quan',
      category: 'Officer',
      reason: `${tgE} khắc ${dmE}, ${samePolarity ? 'cùng' : 'khác'} âm dương`
    };
  }
  if (generatedBy(dmE) === tgE) {
    return {
      name: samePolarity ? 'Thiên Ấn' : 'Chính Ấn',
      category: 'Resource',
      reason: `${tgE} sinh ${dmE}, ${samePolarity ? 'cùng' : 'khác'} âm dương`
    };
  }
  return { name: 'Unknown', category: 'Unknown', reason: 'Không xác định' };
}

// ===== Direction + Start Age =====
export function getDaiVanDirection({ gender, yearCanIndex }) {
  const g = (gender || '').toString().trim().toLowerCase();
  const isMale = g === 'nam' || g === 'male' || g === 'm';
  const isFemale = g === 'nu' || g === 'nữ' || g === 'female' || g === 'f';
  if (!isMale && !isFemale) return null;

  const yearIsYang = YANG_STEM_INDEX.has(yearCanIndex);
  if (isMale) return yearIsYang ? 'forward' : 'backward';
  return yearIsYang ? 'backward' : 'forward';
}

function toMs(x) {
  if (x == null) return null;
  if (typeof x === 'number') return x;
  const d = new Date(x);
  return Number.isFinite(d.getTime()) ? d.getTime() : null;
}

export function computeStartAge({ birthAtLocal, prevTermAtLocal, nextTermAtLocal, direction }) {
  const birthMs = toMs(birthAtLocal);
  const prevMs = toMs(prevTermAtLocal);
  const nextMs = toMs(nextTermAtLocal);
  if (!birthMs || !prevMs || !nextMs || !direction) return null;

  const diffMs = direction === 'forward' ? (nextMs - birthMs) : (birthMs - prevMs);
  if (diffMs <= 0) return null;
  const diffDays = diffMs / MS_PER_DAY;
  const startAge = diffDays / 3;
  return Math.round(startAge * 100) / 100;
}

// ===== Expanded dataset v2.1 =====
export function buildDaiVanDatasetV2({
  tzOffsetMinutes = 420,
  birthYear,
  birthAtLocal,
  birthDateSolar,
  birthTimeLocal,
  gender,

  yearCanIndex, yearChiIndex,
  monthCanIndex, monthChiIndex,
  dayCanIndex, dayChiIndex,
  hourCanIndex, hourChiIndex,

  prevTerm, nextTerm,
  count = 8
}) {
  const normalizedGender = normalizeBinaryGender(gender);
  if (!normalizedGender) {
    throw new Error('Gender is required to compute Dai Van (expected male/female).');
  }

  const directionValue = getDaiVanDirection({ gender: normalizedGender, yearCanIndex });

  const norm = normalizeSolarTermsAroundBirth({
    birthAtLocal,
    prevTerm,
    nextTerm,
    tzOffsetMinutes
  });
  const prevTermNormalized = norm.prevTerm;
  const nextTermNormalized = norm.nextTerm;

  const startAgeValue = computeStartAge({
    birthAtLocal,
    prevTermAtLocal: prevTermNormalized?.at_local,
    nextTermAtLocal: nextTermNormalized?.at_local,
    direction: directionValue
  });

  const birthMs = toMs(birthAtLocal);
  const diffToTerm = (() => {
    const b = birthMs;
    const p = toMs(prevTermNormalized?.at_local);
    const n = toMs(nextTermNormalized?.at_local);
    if (!b || !p || !n || !directionValue) return null;
    const ms = directionValue === 'forward' ? n - b : b - p;
    return { milliseconds: ms, days: ms / MS_PER_DAY };
  })();

  const startAtMs = birthMs != null && Number.isFinite(startAgeValue)
    ? birthMs + startAgeValue * DAYS_PER_TROPICAL_YEAR * MS_PER_DAY
    : null;
  const startAtLocal = startAtMs != null
    ? formatDateWithOffset(new Date(startAtMs), tzOffsetMinutes)
    : null;

  const startOffsetFromBirth = startAtMs != null && birthMs != null
    ? {
        milliseconds: startAtMs - birthMs,
        days: (startAtMs - birthMs) / MS_PER_DAY,
        years: startAgeValue
      }
    : null;

  const meta = {
    version: 'dai-van-meta-v2.1',
    tz_offset_minutes: tzOffsetMinutes,
    input: {
      birth_date_solar: birthDateSolar || null,
      birth_time_local: birthTimeLocal || null,
      birth_at_local: birthAtLocal || null,
      gender: normalizedGender
    },
    pillars: {
      year: { canIndex: yearCanIndex, chiIndex: yearChiIndex, label: `${STEMS[yearCanIndex]}-${BRANCHES[yearChiIndex]}` },
      month: { canIndex: monthCanIndex, chiIndex: monthChiIndex, label: `${STEMS[monthCanIndex]}-${BRANCHES[monthChiIndex]}` },
      day: { canIndex: dayCanIndex, chiIndex: dayChiIndex, label: `${STEMS[dayCanIndex]}-${BRANCHES[dayChiIndex]}` },
      hour: { canIndex: hourCanIndex, chiIndex: hourChiIndex, label: `${STEMS[hourCanIndex]}-${BRANCHES[hourChiIndex]}` }
    },
    direction: {
      value: directionValue,
      rule: 'Nam + năm dương => Thuận; Nam + năm âm => Nghịch; Nữ thì ngược lại',
      year_can_yinyang: stemYinYang(yearCanIndex),
      explain:
        directionValue
          ? `gender=${normalizedGender} & year_can=${STEMS[yearCanIndex]} (${stemYinYang(yearCanIndex)}) => ${directionValue}`
          : 'Thiếu gender hoặc yearCanIndex'
    },
    start_age: {
      value: startAgeValue,
      rounding: 2,
      rule: '(khoảng cách đến tiết khí gần nhất theo chiều vận) / 3 ngày',
      three_days_equals_one_year: 3,
      directional_term_used: directionValue === 'forward' ? 'next_term' : (directionValue === 'backward' ? 'prev_term' : null),
      start_at_local: startAtLocal,
      diff: diffToTerm,
      diff_to_term: diffToTerm,
      start_offset_from_birth: startOffsetFromBirth
    },
    solar_terms: { prev: prevTermNormalized || null, next: nextTermNormalized || null },
    quality: {
      has_gender: true,
      has_prev_next_terms: !!(prevTermNormalized?.at_local && nextTermNormalized?.at_local),
      warnings: []
    }
  };

  if (!meta.quality.has_prev_next_terms) meta.quality.warnings.push('missing_prev_next_solar_terms');
  if (!directionValue) meta.quality.warnings.push('cannot_determine_direction');
  if (startAgeValue == null) meta.quality.warnings.push('cannot_compute_start_age');
  if (directionValue && startAgeValue === 0) meta.quality.warnings.push('start_age_zero_suspect');
  meta.quality.warnings.push(...norm.warnings);

  {
    const b = toMs(birthAtLocal);
    const p = toMs(prevTermNormalized?.at_local);
    const n = toMs(nextTermNormalized?.at_local);
    if (b && p && n) {
      if (n <= b) meta.quality.warnings.push('next_term_not_after_birth');
      if (p >= b) meta.quality.warnings.push('prev_term_not_before_birth');
    }
  }

  const dataset = {
    version: 'dai-van-dataset-v2.1',
    meta_ref: 'dai_van_meta',
    day_master: {
      can: STEMS[dayCanIndex],
      canIndex: dayCanIndex,
      element: stemElement(dayCanIndex),
      yinyang: stemYinYang(dayCanIndex)
    },
    natal: {
      stems: [
        { pillar: 'year', can: STEMS[yearCanIndex], canIndex: yearCanIndex, element: stemElement(yearCanIndex), yinyang: stemYinYang(yearCanIndex) },
        { pillar: 'month', can: STEMS[monthCanIndex], canIndex: monthCanIndex, element: stemElement(monthCanIndex), yinyang: stemYinYang(monthCanIndex) },
        { pillar: 'day', can: STEMS[dayCanIndex], canIndex: dayCanIndex, element: stemElement(dayCanIndex), yinyang: stemYinYang(dayCanIndex) },
        { pillar: 'hour', can: STEMS[hourCanIndex], canIndex: hourCanIndex, element: stemElement(hourCanIndex), yinyang: stemYinYang(hourCanIndex) }
      ],
      branches: [
        { pillar: 'year', chi: BRANCHES[yearChiIndex], chiIndex: yearChiIndex, element: branchElement(yearChiIndex), yinyang: branchYinYang(yearChiIndex) },
        { pillar: 'month', chi: BRANCHES[monthChiIndex], chiIndex: monthChiIndex, element: branchElement(monthChiIndex), yinyang: branchYinYang(monthChiIndex) },
        { pillar: 'day', chi: BRANCHES[dayChiIndex], chiIndex: dayChiIndex, element: branchElement(dayChiIndex), yinyang: branchYinYang(dayChiIndex) },
        { pillar: 'hour', chi: BRANCHES[hourChiIndex], chiIndex: hourChiIndex, element: branchElement(hourChiIndex), yinyang: branchYinYang(hourChiIndex) }
      ]
    },
    periods: []
  };

  if (!directionValue || startAgeValue == null) {
    return { meta, dataset };
  }

  const stepSign = directionValue === 'forward' ? 1 : -1;
  const natalBranches = dataset.natal.branches;
  const natalStems = dataset.natal.stems;
  const startAtDate = startAtMs != null ? new Date(startAtMs) : null;

  dataset.periods = Array.from({ length: count }).map((_, i) => {
    const step = stepSign * (i + 1);

    const canIndex = mod(monthCanIndex + step, 10);
    const chiIndex = mod(monthChiIndex + step, 12);

    const age_from = Math.round((startAgeValue + i * 10) * 100) / 100;
    const age_to = Math.round((startAgeValue + (i + 1) * 10 - 0.01) * 100) / 100;

    const periodStartDate = startAtDate ? shiftUTCYear(startAtDate, i * 10) : null;
    const periodEndDate = startAtDate ? shiftUTCYear(startAtDate, (i + 1) * 10) : null;

    const year_from = periodStartDate ? periodStartDate.getUTCFullYear() : birthYear + Math.floor(age_from);
    const year_to_exclusive = periodEndDate ? periodEndDate.getUTCFullYear() : birthYear + Math.floor(age_to);
    const year_to = Math.max(year_from, year_to_exclusive - 1);
    const year_range = `${year_from}-${year_to}`;
    const year_range_exclusive = `${year_from}-${year_to_exclusive}`;

    const chiName = BRANCHES[chiIndex];
    const canName = STEMS[canIndex];

    const basic = natalBranches
      .map((nb) => {
        const type = relationChiBasic(chiName, nb.chi);
        if (!type) return null;

        const payload = { type, pair: [chiName, nb.chi], hits: [nb.pillar] };
        if (type === 'hop') {
          const hopElement = getLucHopElement(chiName, nb.chi);
          if (hopElement) payload.element = hopElement;
        }
        return payload;
      })
      .filter(Boolean);

    const tamhop = computeTamHopRelations(chiName, natalBranches);
    const extraChiRelations = [];
    if (tamhop.full) extraChiRelations.push(tamhop.full);
    extraChiRelations.push(...tamhop.half);

    const mergedBranchRelations = mergeBranchRelations([
      ...basic,
      ...extraChiRelations
    ]);

    const stemHop = computeStemHopWithNatal(canName, natalStems);

    const hiddenStems = computeHiddenStemsForBranch(chiName, dayCanIndex);

    const hiddenElementWeights = hiddenStems.reduce((acc, hs) => {
      acc[hs.element] = (acc[hs.element] || 0) + hs.weight;
      return acc;
    }, {});

    return {
      index: i,
      step: i + 1,
      direction: directionValue,

      age_from,
      age_to,
      year_from,
      year_to,
      year_to_exclusive,
      year_range,
      year_range_exclusive,
      start_at_local: periodStartDate ? formatDateWithOffset(periodStartDate, tzOffsetMinutes) : null,
      end_at_local: periodEndDate ? formatDateWithOffset(periodEndDate, tzOffsetMinutes) : null,

      pillar: {
        can: canName,
        chi: chiName,
        label: `${canName}-${chiName}`,
        canIndex,
        chiIndex,
        can_element: stemElement(canIndex),
        can_yinyang: stemYinYang(canIndex),
        chi_element: branchElement(chiIndex),
        chi_yinyang: branchYinYang(chiIndex)
      },

      ten_gods: {
        stem_to_day_master: tenGodStem(dayCanIndex, canIndex)
      },

      hidden_stems: {
        list: hiddenStems,
        element_weights: hiddenElementWeights
      },

      relations: {
        with_natal_branches: mergedBranchRelations,
        with_natal_stems: stemHop
      },

      tags: ['10-year-cycle', 'computed']
    };
  });

  return { meta, dataset };
}
